import { TicketAudience, TicketStatusBehavior } from '@21k/shared';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  getApiErrorMessage,
  getTicket,
  getTicketAudit,
  getTicketStatusWorkflow,
  listAssets,
  reopenTicket,
  updateTicketStatus,
} from '../../api/client';
import type {
  Asset,
  AssignableTeamGroup,
  TicketAudit,
  TicketCategoryRef,
  TicketDetail,
  TicketLabelRef,
  TicketStatusWorkflow,
} from '../../api/types';
import type { TicketSocketUpdate } from '@21k/shared';
import { useAuth } from '../../contexts/AuthContext';
import { canEditTicket } from '../../utils/permissions';
import EscalateModal from './EscalateModal';
import AuditTab from './AuditTab';
import FilesTab from './FilesTab';
import OverviewTab from './OverviewTab';
import TicketDetailHeader, { type Tab } from './TicketDetailHeader';
import TicketDetailSidebar from './TicketDetailSidebar';
import { loadTicketEditOptions } from './TicketEditPanel';
import { resolveAllowedStatusIds } from '../../utils/ticket';

type Props = {
  embedded?: boolean;
};

export default function TicketDetailPage({ embedded = false }: Props) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const uploadError =
    location.state &&
    typeof location.state === 'object' &&
    'uploadError' in location.state &&
    typeof location.state.uploadError === 'string'
      ? location.state.uploadError
      : '';
  const { user } = useAuth();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [audits, setAudits] = useState<TicketAudit[]>([]);
  const [workflow, setWorkflow] = useState<TicketStatusWorkflow | null>(null);
  const [labelOptions, setLabelOptions] = useState<TicketLabelRef[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<TicketCategoryRef[]>([]);
  const [assignableTeams, setAssignableTeams] = useState<AssignableTeamGroup[]>([]);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [t, ast, aud, wf] = await Promise.all([
        getTicket(id),
        listAssets(id),
        getTicketAudit(id),
        getTicketStatusWorkflow(),
      ]);
      setTicket(t);
      setAssets(ast);
      setAudits(aud);
      setWorkflow(wf);
      if (user && canEditTicket(user, t)) {
        const opts = await loadTicketEditOptions(t.departmentId);
        setLabelOptions(opts.labelOptions);
        setCategoryOptions(opts.categoryOptions);
        setAssignableTeams(opts.assignableTeams);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleTicketSocketUpdate = useCallback((update: TicketSocketUpdate) => {
    setTicket((prev) => {
      if (!prev || prev.id !== update.ticketId) return prev;
      return {
        ...prev,
        status: update.statusCode
          ? {
              ...prev.status,
              code: update.statusCode,
              name: update.statusLabel ?? prev.status.name,
            }
          : prev.status,
        assignedTo: update.assignedToName
          ? {
              ...prev.assignedTo,
              id: prev.assignedTo?.id ?? '',
              firstName: update.assignedToName.split(' ')[0] ?? update.assignedToName,
              lastName: update.assignedToName.split(' ').slice(1).join(' '),
              email: prev.assignedTo?.email ?? '',
            }
          : prev.assignedTo,
      };
    });
  }, []);

  async function handleClose() {
    if (!ticket || !workflow || ticket.status.isTerminal) return;
    const allowed = resolveAllowedStatusIds(ticket.statusId, workflow);
    const closeStatus = workflow.statuses.find(
      (s) =>
        allowed.includes(s.id) &&
        (s.isTerminal || s.onEnterBehavior === TicketStatusBehavior.COMPLETE),
    );
    if (!closeStatus) {
      setActionError('No close status available from current workflow.');
      return;
    }
    if (!window.confirm('Close this ticket?')) return;
    setClosing(true);
    setActionError('');
    try {
      await updateTicketStatus(ticket.id, closeStatus.id, 'Ticket closed');
      await load();
    } catch (err) {
      setActionError(getApiErrorMessage(err));
    } finally {
      setClosing(false);
    }
  }

  async function handleReopen() {
    if (!ticket) return;
    if (!window.confirm('Reopen this ticket? Status will move back to To Do.')) return;
    setReopening(true);
    setActionError('');
    try {
      await reopenTicket(ticket.id);
      await load();
    } catch (err) {
      setActionError(getApiErrorMessage(err));
    } finally {
      setReopening(false);
    }
  }

  if (loading) {
    return <div className={`ticket-detail-page${embedded ? ' ticket-detail-page--embedded' : ''}`}>
      <div className="loading">Loading ticket…</div>
    </div>;
  }
  if (error || !ticket) {
    return (
      <div className={`ticket-detail-page${embedded ? ' ticket-detail-page--embedded' : ''}`}>
        <div className="alert alert-error">{error || 'Ticket not found'}</div>
      </div>
    );
  }

  const descriptionAssets = assets.filter((a) => !a.commentId);
  const canEdit = Boolean(user && workflow && canEditTicket(user, ticket));
  const canClose = canEdit && !ticket.status.isTerminal;
  const canReopen =
    canEdit &&
    (ticket.status.code === 'CANNOT_RESOLVE' ||
      ticket.status.onEnterBehavior === TicketStatusBehavior.CANNOT_RESOLVE);
  const showFilesTab = ticket.audience === TicketAudience.INTERNAL;
  const activeAssigneeId = ticket.currentOwner?.assignedToId ?? ticket.assignedToId;

  const tabItems: Array<readonly [Tab, string]> = [
    ['overview', 'Overview'],
    ...(showFilesTab ? [['files', `Files (${assets.length})`] as const] : []),
    ['audit', `Audit Log (${audits.length})`],
  ];

  return (
    <div className={`ticket-detail-page${embedded ? ' ticket-detail-page--embedded' : ''}`}>
      <div className="ticket-detail-layout">
        <div className="ticket-detail-main-panel">
          <TicketDetailHeader
            ticket={ticket}
            embedded={embedded}
            onEscalate={() => setEscalateOpen(true)}
            onClose={() => void handleClose()}
            onReopen={() => void handleReopen()}
            closing={closing}
            reopening={reopening}
            canClose={canClose}
            canReopen={canReopen}
            tab={tab}
            tabItems={tabItems}
            onTabChange={setTab}
          />

          {uploadError && (
            <div className="ticket-detail-alert alert alert-error">
              Ticket created, but the attachment failed: {uploadError}
            </div>
          )}

          {actionError && (
            <div className="ticket-detail-alert alert alert-error">{actionError}</div>
          )}

          <div className="ticket-detail-main">
            {tab === 'overview' && (
              <OverviewTab
                ticketId={ticket.id}
                audience={ticket.audience}
                description={ticket.description}
                descriptionAssets={descriptionAssets}
                activeAssigneeId={activeAssigneeId}
                onRefresh={() => void load()}
                onTicketUpdate={handleTicketSocketUpdate}
              />
            )}
            {tab === 'files' && showFilesTab && (
              <div className="ticket-detail-tab-scroll">
                <FilesTab
                  ticketId={ticket.id}
                  assets={assets}
                  onRefresh={() => void load()}
                />
              </div>
            )}
            {tab === 'audit' && (
              <div className="ticket-detail-tab-scroll">
                <AuditTab audits={audits} />
              </div>
            )}
          </div>
        </div>

        {tab === 'overview' && (
          <aside className="ticket-detail-sidebar">
            <TicketDetailSidebar
              ticket={ticket}
              canEdit={canEdit}
              workflow={workflow}
              labelOptions={labelOptions}
              categoryOptions={categoryOptions}
              assignableTeams={assignableTeams}
              onUpdated={() => void load()}
              onError={(msg) => setActionError(msg)}
              onReopen={() => void handleReopen()}
              reopening={reopening}
            />
          </aside>
        )}
      </div>

      <EscalateModal
        ticket={ticket}
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        onDone={() => void load()}
      />
    </div>
  );
}
